/**
 * Custom Menu Links — Super Admin only.
 * CRUD for sidebar links that point to external URLs / iframes.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit2, Trash2, ExternalLink, MonitorPlay, RefreshCw, ArrowUp, ArrowDown, ListOrdered } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ICON_OPTIONS = [
  'link', 'landmark', 'credit-card', 'building', 'file-text', 'globe',
  'shopping-cart', 'graduation-cap', 'shield', 'zap', 'monitor', 'briefcase',
];
const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'iso', label: 'ISO' },
  { value: 'iso_agent', label: 'ISO Agent' },
  { value: 'iso_subagent', label: 'ISO Subagent' },
];
const OPEN_MODES = [
  { value: 'new_tab', label: 'Open in New Tab', icon: ExternalLink },
  { value: 'current_window', label: 'Open in Current Window', icon: RefreshCw },
  { value: 'iframe', label: 'Embed as iFrame in Current Window', icon: MonitorPlay },
];

export default function MenuLinksAdmin() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogLink, setDialogLink] = useState(null);
  const [rearrangeMode, setRearrangeMode] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = async () => {
    try {
      const d = await api.listMenuLinks();
      setLinks(d || []);
    } catch (e) {
      toast.error('Failed to load menu links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this menu link?')) return;
    try {
      await api.deleteMenuLink(id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const move = (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= links.length) return;
    const next = [...links];
    [next[idx], next[target]] = [next[target], next[idx]];
    setLinks(next);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.request('/menu-links/reorder', {
        method: 'POST',
        body: JSON.stringify({ ordered_ids: links.map((l) => l.id) }),
      });
      toast.success('Order saved');
      setRearrangeMode(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="menu-links-admin">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold">Custom Menu Links</h1>
          <p className="text-sm text-muted-foreground">
            Add custom sidebar links to external portals or embedded pages. Choose which roles and tenants see each one.
          </p>
        </div>
        <div className="flex gap-2">
          {rearrangeMode ? (
            <>
              <Button variant="outline" onClick={() => { setRearrangeMode(false); load(); }}>Cancel</Button>
              <Button onClick={saveOrder} disabled={savingOrder} data-testid="menu-links-save-order-btn">
                {savingOrder ? 'Saving…' : 'Save Order'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setRearrangeMode(true)} data-testid="menu-links-rearrange-btn">
                <ListOrdered className="h-4 w-4 mr-1" /> Rearrange
              </Button>
              <Button onClick={() => setDialogLink({})} data-testid="menu-links-new-btn">
                <Plus className="h-4 w-4 mr-1" /> Create New
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {links.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">
              No custom menu links yet. Create one to give quick access to processor portals, dashboards, or embedded tools.
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3">URL</th>
                  <th className="text-left p-3">Open Mode</th>
                  <th className="text-left p-3">Visible To</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {links.map((l, idx) => (
                  <tr key={l.id} className="border-b last:border-0 text-sm" data-testid={`menu-link-${l.id}`}>
                    <td className="p-3 font-medium">
                      {rearrangeMode && (
                        <span className="inline-flex items-center gap-1 mr-2">
                          <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-muted rounded disabled:opacity-30">
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button onClick={() => move(idx, 1)} disabled={idx === links.length - 1} className="p-1 hover:bg-muted rounded disabled:opacity-30">
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                      {l.title}
                    </td>
                    <td className="p-3 text-muted-foreground truncate max-w-xs">{l.url}</td>
                    <td className="p-3 text-xs">
                      <Badge variant="secondary">
                        {OPEN_MODES.find(m => m.value === l.open_mode)?.label || l.open_mode}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(l.visible_to_roles || []).map((r) => (
                          <Badge key={r} variant="outline" className="text-[10px]">{r.replace('_', ' ')}</Badge>
                        ))}
                        {l.visible_to_tenant_ids && l.visible_to_tenant_ids.length > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/10">
                            {l.visible_to_tenant_ids.length} tenant{l.visible_to_tenant_ids.length === 1 ? '' : 's'}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      {!rearrangeMode && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setDialogLink(l)}>
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => del(l.id)}>
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {dialogLink && (
        <MenuLinkDialog
          link={dialogLink}
          onClose={() => setDialogLink(null)}
          onSaved={() => { setDialogLink(null); load(); }}
        />
      )}
    </div>
  );
}

function MenuLinkDialog({ link, onClose, onSaved }) {
  const isEdit = !!link.id;
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState({
    title: link.title || '',
    icon: link.icon || 'link',
    url: link.url || '',
    open_mode: link.open_mode || 'new_tab',
    visible_to_roles: link.visible_to_roles || ['super_admin', 'iso', 'iso_agent', 'iso_subagent'],
    visible_to_tenant_ids: link.visible_to_tenant_ids || null,
    allow_microphone: link.allow_microphone || false,
    allow_camera: link.allow_camera || false,
    sort_order: link.sort_order || 0,
  });
  const [tenantMode, setTenantMode] = useState(link.visible_to_tenant_ids ? 'specific' : 'all');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getTenants().then((d) => setTenants(d || [])).catch(() => setTenants([]));
  }, []);

  const toggleRole = (r) => setForm(f => ({
    ...f,
    visible_to_roles: f.visible_to_roles.includes(r)
      ? f.visible_to_roles.filter(x => x !== r)
      : [...f.visible_to_roles, r],
  }));

  const toggleTenant = (tid) => setForm(f => {
    const cur = f.visible_to_tenant_ids || [];
    const next = cur.includes(tid) ? cur.filter(x => x !== tid) : [...cur, tid];
    return { ...f, visible_to_tenant_ids: next };
  });

  const save = async () => {
    if (!form.title || !form.url) {
      toast.error('Title and URL required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        visible_to_tenant_ids: tenantMode === 'all' ? null : (form.visible_to_tenant_ids || []),
      };
      if (isEdit) await api.updateMenuLink(link.id, payload);
      else await api.createMenuLink(payload);
      toast.success(isEdit ? 'Updated' : 'Created');
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="menu-link-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Menu Link' : 'Create Menu Link'}</DialogTitle>
          <DialogDescription>Customize your sidebar with a custom link to any URL or embedded page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Icon</Label>
            <Select value={form.icon} onValueChange={(v) => setForm(f => ({ ...f, icon: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ICON_OPTIONS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="TSYS Portal" data-testid="menu-link-title" />
          </div>
          <div>
            <Label>URL *</Label>
            <Input value={form.url} onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
              placeholder="https://portal.tsys.com" data-testid="menu-link-url" />
          </div>
          <div>
            <Label>When Clicked</Label>
            <div className="space-y-2 mt-1">
              {OPEN_MODES.map((m) => {
                const MIcon = m.icon;
                const active = form.open_mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, open_mode: m.value }))}
                    className={`w-full flex items-center gap-2 p-2 rounded border text-left text-sm ${active ? 'border-primary bg-primary/5' : 'border-muted'}`}
                  >
                    <MIcon className="h-4 w-4 opacity-70" />
                    <span className="flex-1">{m.label}</span>
                    {active && <div className="h-2 w-2 rounded-full bg-primary" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label>Role-Based Visibility</Label>
            <div className="space-y-1 mt-1">
              {ROLES.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.visible_to_roles.includes(r.value)} onCheckedChange={() => toggleRole(r.value)} />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Tenant Visibility</Label>
            <div className="space-y-2 mt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={tenantMode === 'all'}
                  onChange={() => setTenantMode('all')} data-testid="tenant-mode-all" />
                All tenants (default)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" checked={tenantMode === 'specific'}
                  onChange={() => setTenantMode('specific')} data-testid="tenant-mode-specific" />
                Only these tenants…
              </label>
              {tenantMode === 'specific' && (
                <div className="border rounded p-2 max-h-40 overflow-y-auto space-y-1">
                  {tenants.length === 0 && <p className="text-xs text-muted-foreground">No tenants loaded.</p>}
                  {tenants.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={(form.visible_to_tenant_ids || []).includes(t.id)}
                        onCheckedChange={() => toggleTenant(t.id)}
                      />
                      <span className="truncate">{t.company_name || t.dba_name || t.name || t.tenant_name || t.id}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          {form.open_mode === 'iframe' && (
            <div className="border-t pt-3">
              <Label className="mb-2 block">iFrame Permissions</Label>
              <label className="flex items-center gap-2 mb-1">
                <Switch checked={form.allow_microphone} onCheckedChange={(v) => setForm(f => ({ ...f, allow_microphone: v }))} />
                <span className="text-sm">Allow Microphone</span>
              </label>
              <label className="flex items-center gap-2">
                <Switch checked={form.allow_camera} onCheckedChange={(v) => setForm(f => ({ ...f, allow_camera: v }))} />
                <span className="text-sm">Allow Camera</span>
              </label>
            </div>
          )}
          <div>
            <Label>Sort Order</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value || '0') }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="menu-link-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
